import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Asterisk, Sparkles, Users, BookOpen, Star, PlayCircle } from "lucide-react";
import { IMAGES, COURSES } from "@/data/content";

const Badge = ({ icon: I, value, label, className, delay }) => (
  <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay, duration: 0.6 }}
    className={`glass pulse-glow absolute rounded-2xl px-4 py-3 flex items-center gap-3 ${className}`}>
    <div className="icon-orb !w-10 !h-10"><I size={18} /></div>
    <div><p className="font-bold text-lg leading-none">{value}</p><p className="text-xs text-[var(--muted)]">{label}</p></div>
  </motion.div>
);

export const Hero = () => (
  <section data-testid="section-hero" className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-12 md:pt-20 pb-12 grid lg:grid-cols-12 gap-12 items-center">
    <div className="grid-lines -z-10" />
    <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
      <span className="eyebrow">A New Era of Possibilities</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-6 leading-[1.08]">
        Her Hijab. Her Skills.<br /><span className="neon-text">Her Success.</span>
      </h1>
      <p className="mt-6 text-base md:text-lg text-[var(--muted)] max-w-xl leading-relaxed">
        Ilmora Academy helps girls and women learn job-ready digital skills from home, in a safe, women-first space. Seekho apni speed se, kamao apni shart par.
      </p>
      <div className="flex flex-wrap gap-4 mt-9">
        <Link to="/courses" data-testid="hero-explore-btn" className="btn-neon"><Sparkles size={17} /> Explore Courses</Link>
        <Link to="/packages" data-testid="hero-packages-btn" className="btn-ghost"><PlayCircle size={17} /> View Packages</Link>
      </div>
      <div className="flex items-center gap-3 mt-10 text-sm text-[var(--muted)]">
        <div className="flex text-[var(--star)]">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} fill="currentColor" />)}</div>
        Loved by 12,000+ learners across India
      </div>
    </motion.div>
    <div className="lg:col-span-5 relative">
      <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }}
        className="relative aspect-[4/5] flex items-end justify-center">
        <div aria-hidden className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[88%] aspect-square rounded-full blur-2xl opacity-60" style={{ background: "radial-gradient(circle, var(--glow-pink), transparent 70%)" }} />
        <div aria-hidden className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[78%] aspect-square rounded-full border border-[var(--surface-border)]" style={{ background: "radial-gradient(circle at 50% 35%, color-mix(in srgb, var(--pink) 18%, transparent), transparent 70%)" }} />
        <img data-testid="hero-image" src={IMAGES.hero} alt="Ilmora Academy student" className="relative h-full w-auto object-contain object-bottom" style={{ maskImage: "linear-gradient(to bottom, black 85%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, black 85%, transparent)" }} />
      </motion.div>
      <Badge icon={Users} value="12K+" label="Active learners" className="-left-4 sm:-left-10 top-10" delay={0.5} />
      <Badge icon={BookOpen} value="36+" label="Skill courses" className="-right-2 sm:-right-6 bottom-16" delay={0.7} />
    </div>
  </section>
);

export const SkillStrip = () => (
  <div data-testid="skills-strip" className="relative py-4 overflow-hidden my-8 text-[var(--strip-ink)]" style={{ background: "var(--strip-bg)" }}>
    <div className="marquee" style={{ animationDuration: "140s" }}>
      {[...COURSES, ...COURSES].map(({ title: s }, i) => (
        <span key={i} className="flex items-center gap-8 px-8 font-display text-lg md:text-xl font-semibold whitespace-nowrap">
          {s} <Asterisk size={26} strokeWidth={3} />
        </span>
      ))}
    </div>
  </div>
);
